import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { HttpUtils } from '../../shared/http-utils';
import { URL_BASE } from '../../core/constants';
import { map } from 'rxjs/operators';
import { Region } from './region.model';
import { SortDirection } from '../../shared/sort-direction';
import { SearchObjectResult } from '../../shared/search-object-result.model';
import { RegionSearchCriteria } from './region-search-criteria.model';

@Injectable()
export class RegionService {

  private REGIONS_URI = 'regions';

  constructor(private http: HttpClient) {
  }

  public search(
    page: number, size: number, sortColumn: string, direction: SortDirection, criteria: RegionSearchCriteria
  ): Observable<SearchObjectResult<Region>> {
    for (const key of Object.keys(criteria)) {
      if (!criteria[key]) {
        delete criteria[key];
      }
    }

    const headers = HttpUtils.createHeaders();
    const params  = {
      page: String(page),
      size: String(size),
      sort: sortColumn,
      direction: direction,
      ...criteria
    };

    const url = `${URL_BASE}/api/${this.REGIONS_URI}/search`;
    return this.http.get(url, { headers, params })
      .pipe(
        map(result => SearchObjectResult.fromDto(result, Region.fromDto))
      );
  }

  public getAll(): Observable<Region[]> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/api/${this.REGIONS_URI}`;

    return this.http.get(url, { headers })
      .pipe(
        map((regions: any[]) => regions.map(p => Region.fromDto(p)))
      );
  }

  public getAllLight(): Observable<Region[]> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/api/${this.REGIONS_URI}/light`;

    return this.http.get(url, { headers })
      .pipe(
        map((regions: any[]) => regions.map(p => Region.fromDto(p)))
      );
  }

  public getById(regionId: string): Observable<Region> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/api/${this.REGIONS_URI}/${regionId}`;

    return this.http.get(url, { headers })
      .pipe(
        map(r => Region.fromDto(r))
      );
  }

  public getByCarrier(carrierId: string): Observable<Region> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/api/${this.REGIONS_URI}/by/carrier/${carrierId}`;

    return this.http.get(url, { headers })
      .pipe(
        map(r => Region.fromDto(r))
      );
  }

  public getOwnRegions(): Observable<Region[]> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/api/${this.REGIONS_URI}/self`;

    return this.http.get(url, { headers })
      .pipe(
        map((regions: any[]) => regions.map(p => Region.fromDto(p)))
      );
  }

  public getOwnRegionsLight(): Observable<Region[]> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/api/${this.REGIONS_URI}/self-light`;

    return this.http.get(url, { headers })
      .pipe(
        map((regions: any[]) => regions.map(p => Region.fromDto(p)))
      );
  }

  public create(region: Region): Observable<Region> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/api/${this.REGIONS_URI}`;

    return this.http.post(url, Region.toDto(region), { headers })
      .pipe(
        map(r => Region.fromDto(r))
      );
  }

  public update(region: Region): Observable<Region> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/api/${this.REGIONS_URI}/${region.id}`;

    return this.http.put(url, Region.toDto(region), { headers })
      .pipe(
        map(r => Region.fromDto(r))
      );
  }

  public remove(id: string): Observable<void> {
    const headers = HttpUtils.createHeaders();
    const url     = `${URL_BASE}/api/${this.REGIONS_URI}/${id}`;

    return this.http.delete<void>(url, { headers });
  }
}
